import { useState } from "react";
import { ClipboardList, Lock, Trash2 } from "lucide-react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { toast } from "sonner";

import { Badge } from "@/components/_atoms/badge";
import {
  Menubar,
  MenubarContent,
  MenubarItem,
  MenubarMenu,
  MenubarTrigger,
} from "@/components/_atoms/menubar";
import { DeleteConfirmation } from "@/components/_molecules/delete-confirmation";
import { ErrorView } from "@/components/_molecules/error-view";
import { Loading } from "@/components/_molecules/loading-view";
import { MenuDotsButton } from "@/components/_molecules/menu-dots-button";
import { HeaderLayout } from "@/components/_templates/header-layout";
import { useDomainTranslation } from "@/domain/_shared/i18n/use-domain-translation";
import { UserFavoriteButton } from "@/domain/user/components/user-favorite-button";
import { useCurrentUserContext } from "@/domain/user/hooks/use-current-user-context";
import { useCurrentWorkspaceContext } from "@/domain/workspace/hooks/useCurrentWorkspaceContext";
import { useDocumentTitle } from "@/hooks/use-document-title";

import { DataRequestEntityList } from "../components/data-request-entity-list/data-request-entity-list";
import { useCloseDataRequestMutation } from "../hooks/api/use-close-data-request";
import { useDeleteDataRequestMutation } from "../hooks/api/use-delete-data-request";
import { useGetDataRequestQuery } from "../hooks/api/use-get-data-request";
import { toDisplayDueDate } from "../lib/due-date";

export const DataRequestDetailPage = () => {
  const { dataRequestId } = useParams<{ dataRequestId: string }>();
  const { currentWorkspace } = useCurrentWorkspaceContext();
  const { currentUser } = useCurrentUserContext();
  const { t, i18n } = useDomainTranslation("data-request");
  const navigate = useNavigate();
  const [isCloseConfirmOpen, setIsCloseConfirmOpen] = useState(false);
  const [isDeleteConfirmOpen, setIsDeleteConfirmOpen] = useState(false);

  const { dataRequest, loading, error } = useGetDataRequestQuery({
    id: dataRequestId,
    workspaceId: currentWorkspace?.id,
  });
  useDocumentTitle(dataRequest?.title ?? t("list.title"));

  const { closeDataRequest } = useCloseDataRequestMutation({
    onCompleted: () => {
      toast.success(t("detail.closeSuccess"));
      navigate("..");
    },
    onError: () => {
      toast.error(t("detail.closeError"));
    },
  });

  const { deleteDataRequest } = useDeleteDataRequestMutation({
    onCompleted: () => {
      toast.success(t("detail.deleteSuccess"));
      navigate("..");
    },
    onError: () => {
      toast.error(t("detail.deleteError"));
    },
  });

  if (loading) return <Loading />;
  if (error || !dataRequest) return <ErrorView />;

  const statusVariant = dataRequest.status === "OPEN" ? "default" : "secondary";
  const isOpen = dataRequest.status === "OPEN";
  const isCreator = dataRequest.createdById === currentUser.id;
  const isSuperUser = !!currentUser.superUser;
  const isWorkspaceAdmin = currentWorkspace?.workspaceMembership?.role === "ADMIN";
  const canDelete = isCreator || isSuperUser || isWorkspaceAdmin;

  return (
    <div className="flex h-full max-h-full flex-col overflow-hidden">
      <HeaderLayout>
        <div className="flex flex-row items-center gap-2">
          <Link to="..">
            <div className="flex flex-row items-center overflow-hidden truncate text-ellipsis whitespace-nowrap">
              <div className="mr-2 h-3 w-3">
                <ClipboardList className="h-3 w-3" />
              </div>
              <span className="overflow-hidden text-ellipsis whitespace-nowrap">
                {t("list.title")}
              </span>
            </div>
          </Link>
          <div>›</div>
          <div className="block max-w-48 overflow-hidden truncate text-ellipsis whitespace-nowrap">
            {dataRequest.title}
          </div>
          <UserFavoriteButton pageName={dataRequest.title} />
          {dataRequest.createdBy?.fullname && (
            <span className="text-muted-foreground">
              {t("list.columns.createdBy")}: {dataRequest.createdBy.fullname}
            </span>
          )}
          {dataRequest.dueDate && (
            <span className="text-muted-foreground">
              {t("list.columns.dueDate")}:{" "}
              {toDisplayDueDate(dataRequest.dueDate).toLocaleDateString(i18n.language)}
            </span>
          )}
        </div>
        <div className="mr-4 ml-auto flex flex-row items-center gap-2">
          {canDelete && (
            <Menubar className="rounded-md shadow-none" role="menu" aria-label={t("detail.actions")}>
              <MenubarMenu>
                <MenubarTrigger asChild>
                  <MenuDotsButton aria-label={t("detail.openActions")} />
                </MenubarTrigger>
                <MenubarContent className="rounded-md">
                  {isCreator && isOpen && (
                    <MenubarItem onClick={() => setIsCloseConfirmOpen(true)}>
                      <div className="flex flex-row items-center gap-1">
                        <Lock className="mr-2 h-3 w-3 text-muted-foreground" />
                        <span>{t("detail.close")}</span>
                      </div>
                    </MenubarItem>
                  )}
                  <MenubarItem onClick={() => setIsDeleteConfirmOpen(true)}>
                    <div className="flex flex-row items-center gap-1">
                      <Trash2 className="mr-2 h-3 w-3 text-destructive" />
                      <span className="text-destructive">{t("detail.delete")}</span>
                    </div>
                  </MenubarItem>
                </MenubarContent>
              </MenubarMenu>
            </Menubar>
          )}
          <Badge variant={statusVariant}>{t(`status.${dataRequest.status}`)}</Badge>
        </div>
      </HeaderLayout>

      <DataRequestEntityList
        entityRequests={dataRequest.entityRequests ?? []}
        workspace={currentWorkspace}
        fieldSelection={dataRequest.fieldSelection}
        enableDocuments={dataRequest.enableDocuments}
        freeTextQuestions={dataRequest.freeTextQuestions}
      />

      <DeleteConfirmation
        open={isCloseConfirmOpen}
        onOpenChange={setIsCloseConfirmOpen}
        onConfirm={() => {
          closeDataRequest({ id: dataRequest.id, workspaceId: currentWorkspace.id });
        }}
        title={t("detail.closeConfirmTitle")}
        description={t("detail.closeConfirmDescription")}
      />

      <DeleteConfirmation
        open={isDeleteConfirmOpen}
        onOpenChange={setIsDeleteConfirmOpen}
        onConfirm={() => {
          deleteDataRequest({ id: dataRequest.id, workspaceId: currentWorkspace.id });
        }}
        title={t("detail.deleteConfirmTitle")}
        description={t("detail.deleteConfirmDescription")}
      />
    </div>
  );
};
